import React from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import handleOpenWhatsApp from 'services/whatsappService';
import {
  GUOMEI,
  adsPackages,
  buildAdsPlanWhatsApp,
  WHATSAPP_PHONE,
} from '../guomeiContent';

const formatMoney = (n) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);

const AdsPackages = () => (
  <Box
    component="section"
    id="planes"
    py={{ xs: 5.5, md: 8 }}
    px={{ xs: 2.5, sm: 4, md: 7 }}
    bgcolor={GUOMEI.white}
  >
    <Box maxWidth={1100} mx="auto">
      <Typography
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          fontSize: '0.7rem',
          color: GUOMEI.lagoon,
          mb: 1,
        }}
      >
        {adsPackages.overline}
      </Typography>
      <Typography
        component="h2"
        sx={{
          fontFamily: '"Fraunces", "Georgia", serif',
          fontWeight: 700,
          fontSize: { xs: '1.7rem', md: '2.35rem' },
          color: GUOMEI.ink,
          mb: 1,
          maxWidth: 520,
          lineHeight: 1.15,
        }}
      >
        {adsPackages.title}
      </Typography>
      <Typography
        sx={{
          fontFamily: '"Source Sans 3", sans-serif',
          color: GUOMEI.inkMuted,
          fontSize: '1.05rem',
          mb: { xs: 3.5, md: 4.5 },
        }}
      >
        {adsPackages.subtitle}
      </Typography>

      <Grid container spacing={2.25} alignItems="stretch">
        {adsPackages.plans.map((plan) => (
          <Grid item xs={12} md={4} key={plan.id}>
            <Box
              height="100%"
              display="flex"
              flexDirection="column"
              p={{ xs: 2.5, md: 3 }}
              borderRadius={2}
              position="relative"
              bgcolor={plan.featured ? GUOMEI.ocean : GUOMEI.sand}
              color={plan.featured ? GUOMEI.white : GUOMEI.ink}
              border={plan.featured ? 'none' : `1px solid ${GUOMEI.sandDeep}`}
            >
              {plan.badge ? (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 14,
                    right: 14,
                    px: 1.1,
                    py: 0.35,
                    borderRadius: 1,
                    bgcolor: GUOMEI.sunset,
                    color: GUOMEI.white,
                    fontSize: 11,
                    fontWeight: 800,
                    fontFamily: '"Outfit", sans-serif',
                  }}
                >
                  {plan.badge}
                </Box>
              ) : null}

              <Typography
                sx={{
                  fontFamily: '"Outfit", sans-serif',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: plan.featured ? GUOMEI.sunsetSoft : GUOMEI.lagoon,
                  mb: 0.75,
                }}
              >
                {plan.tag}
              </Typography>

              <Typography
                component="h3"
                sx={{
                  fontFamily: '"Fraunces", "Georgia", serif',
                  fontWeight: 700,
                  fontSize: '1.85rem',
                  lineHeight: 1,
                  mb: 0.75,
                }}
              >
                {plan.name}
              </Typography>

              <Typography
                sx={{
                  fontFamily: '"Source Sans 3", sans-serif',
                  fontSize: '0.98rem',
                  lineHeight: 1.4,
                  opacity: 0.88,
                  mb: 2,
                  minHeight: 40,
                }}
              >
                {plan.focus}
              </Typography>

              <Box display="flex" alignItems="baseline" mb={2.25} sx={{ gap: 0.5 }}>
                <Typography
                  sx={{
                    fontFamily: '"Fraunces", "Georgia", serif',
                    fontWeight: 700,
                    fontSize: '2.75rem',
                    lineHeight: 1,
                  }}
                >
                  {formatMoney(plan.price)}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Outfit", sans-serif',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    opacity: 0.75,
                  }}
                >
                  {plan.priceNote}
                </Typography>
              </Box>

              <Box
                display="grid"
                gridTemplateColumns="repeat(3, 1fr)"
                mb={2.25}
                sx={{
                  gap: 0.75,
                  p: 1.25,
                  borderRadius: 1.5,
                  bgcolor: plan.featured
                    ? 'rgba(255,255,255,0.08)'
                    : GUOMEI.white,
                }}
              >
                {plan.stats.map((stat) => (
                  <Box key={stat.label} textAlign="center">
                    <Typography
                      sx={{
                        fontFamily: '"Outfit", sans-serif',
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        opacity: 0.7,
                        mb: 0.35,
                      }}
                    >
                      {stat.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Outfit", sans-serif',
                        fontWeight: 800,
                        fontSize: '0.88rem',
                        lineHeight: 1.2,
                      }}
                    >
                      {stat.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box
                component="ul"
                sx={{
                  m: 0,
                  mb: 2.75,
                  pl: 0,
                  listStyle: 'none',
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.85,
                }}
              >
                {plan.features.map((feature) => (
                  <Typography
                    component="li"
                    key={feature}
                    sx={{
                      fontFamily: '"Source Sans 3", sans-serif',
                      fontSize: '0.95rem',
                      lineHeight: 1.35,
                      pl: 1.75,
                      position: 'relative',
                      opacity: plan.featured ? 0.94 : 1,
                      color: plan.featured ? GUOMEI.white : GUOMEI.inkMuted,
                      '&::before': {
                        content: '"·"',
                        position: 'absolute',
                        left: 0,
                        fontWeight: 800,
                        color: plan.featured ? GUOMEI.sunsetSoft : GUOMEI.sunset,
                      },
                    }}
                  >
                    {feature}
                  </Typography>
                ))}
              </Box>

              <Button
                variant="contained"
                fullWidth
                startIcon={<WhatsAppIcon />}
                onClick={() =>
                  handleOpenWhatsApp(
                    null,
                    buildAdsPlanWhatsApp(plan.name),
                    WHATSAPP_PHONE,
                  )
                }
                sx={{
                  py: 1.3,
                  borderRadius: 1.5,
                  fontWeight: 800,
                  fontFamily: '"Outfit", sans-serif',
                  textTransform: 'none',
                  bgcolor: plan.featured ? GUOMEI.sunset : GUOMEI.ocean,
                  color: GUOMEI.white,
                  '&:hover': {
                    bgcolor: plan.featured ? GUOMEI.sunsetSoft : GUOMEI.oceanDeep,
                    color: plan.featured ? GUOMEI.oceanDeep : GUOMEI.white,
                  },
                }}
              >
                {plan.cta}
              </Button>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  </Box>
);

export default AdsPackages;
